/**
 * NoEvidenceExplanation Component
 *
 * Displays an informational panel explaining what "no evidence found" means
 * for a specific criterion. Clarifies that absence of evidence is not
 * evidence of absence.
 */

import { Info } from 'lucide-react';

interface NoEvidenceExplanationProps {
  criterionText: string;
}

export function NoEvidenceExplanation({ criterionText }: NoEvidenceExplanationProps) {
  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <Info size={18} className="text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
            No Evidence Found in Documents
          </h4>
          <div className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
            <p>
              The AI could not find evidence related to this criterion in the provided documents:
            </p>
            <p className="italic border-l-2 border-blue-300 dark:border-blue-700 pl-2 text-blue-600 dark:text-blue-400">
              "{criterionText}"
            </p>
            <div className="pt-2 space-y-1">
              <p className="font-medium">Important:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-600 dark:text-blue-400">
                <li>
                  This does <strong>NOT</strong> mean the patient does not have this condition
                </li>
                <li>
                  The information may be in documents not uploaded to this review
                </li>
                <li>
                  Please review the source documents directly to confirm
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
